import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import client from "@/client";

import { PrimaryButton } from "@/pages/shared/shared.styles";

import DateFilter from "@/components/filters/date";
import TraineesFilter from "@/components/filters/trainees";
import { ChevronDownSquareCornersIcon24, DownloadIcon24, FileIcon24 } from "@/components/icons";
import { VRIntlProviderComponent } from "@/components/providers/intl-provider";
import Loader from "@/components/ui/loader";
import Page from "@/components/ui/page";

import base from "@/themes/base/base";

import { FiltersList } from "@/enums/dashboard";

import {
    Container,
    Text,
    Title,
    FileTypeToggle,
    ToggleWrapper,
    Wrapper,
    LoaderContainer,
} from "./reports.styles";

const localeFn = (target: string) => import(`./locale/${target.toLowerCase()}.json`);

function getFilenameFromContentDisposition(headerValue?: string) {
    if (!headerValue) return null;

    // examples:
    // attachment; filename="SessionList_23012026_16.05.xlsx"
    // attachment; filename=SessionList_23012026_16.05.xlsx
    const match = /filename\*?=(?:UTF-8''|")?([^;"\n"]+)/i.exec(headerValue);

    if (!match?.[1]) return null;

    try {
        return decodeURIComponent(match[1].replace(/"/g, "").trim());
    } catch {
        return match[1].replace(/"/g, "").trim();
    }
}

function downloadBlob(data: BlobPart, filename: string, mimeType?: string) {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";

    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
}

const Reports = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(window.location.search);
    const dateRange = queryParams.get("dateRange");
    const currentPath = `${location.pathname}?dateRange=last7days`;

    const [openFilter, setOpenFilter] = useState<FiltersList>(FiltersList.None);
    const [disabled, setDisabled] = useState(false);

    const [filters, setFilters] = useState<{
        userId: string | null;
        from: Date;
        to: Date;
    }>({
        userId: null,
        from: new Date(),
        to: new Date(),
    });

    useEffect(() => {
        navigate(currentPath);
    }, []);

    const handleOnClick = (filterType: FiltersList) => {
        setOpenFilter((prevFilter) => {
            return prevFilter === filterType ? FiltersList.None : filterType;
        });
    };

    const handleDateChange = (from: Date, to: Date) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            from,
            to,
        }));
    };

    const handleTraineeChange = (user: string | null) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            userId: user,
        }));
    };

    const handleDownload = async () => {
        setDisabled(true);

        try {
            const response = await client.get("/v1/Report/download", {
                params: {
                    from: filters.from?.toISOString(),
                    to: filters.to?.toISOString(),
                    userId: filters.userId ?? undefined,
                    // endpoint defaults to xlsx.. keep implicit until we design the file type selector
                },
                responseType: "blob",
            });

            const contentType =
                (response.headers && (response.headers["content-type"] as string)) ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            const filename =
                getFilenameFromContentDisposition(
                    response.headers && (response.headers["content-disposition"] as string),
                ) || "SessionList.xlsx";

            downloadBlob(response.data, filename, contentType);

            handleTraineeChange(null);
        } catch (err) {
            // axios interceptor will handle 401s
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            setDisabled(false);
        }
    };

    return (
        <VRIntlProviderComponent localeFn={localeFn} id="reports-page" fallback={null}>
            <Page title="Reports">
                <Wrapper>
                    <Container>
                        <Title>
                            <FormattedMessage id={"reports:container-title"} />
                        </Title>

                        <ToggleWrapper>
                            <FileTypeToggle fullWidth disabled>
                                <FileIcon24 />
                                <Text>
                                    <FormattedMessage id={"reports:filetype:xslx"} />
                                </Text>
                                <ChevronDownSquareCornersIcon24 />
                            </FileTypeToggle>

                            <TraineesFilter
                                isOpen={openFilter === FiltersList.TraineesList}
                                backgroundColor={base.colors.neutral.grey[900]}
                                handleOnClick={() => handleOnClick(FiltersList.TraineesList)}
                                onUserSelected={handleTraineeChange}
                                initialUserId={filters.userId}
                                fullWidth
                            />

                            <DateFilter
                                isOpen={openFilter === FiltersList.DatePicker}
                                backgroundColor={base.colors.neutral.grey[900]}
                                handleOnClick={() => handleOnClick(FiltersList.DatePicker)}
                                onChange={handleDateChange}
                                initialDateRange={{ from: filters.from, to: filters.to }}
                                dateRange={dateRange}
                                fullWidth
                            />
                        </ToggleWrapper>

                        <PrimaryButton
                            fullWidth
                            onClick={(_e) => {
                                handleDownload();
                            }}
                            disabled={disabled}
                        >
                            {disabled ? (
                                <LoaderContainer>
                                    <Loader size="medium" color="#666666" fontSize="1.5rem" />
                                </LoaderContainer>
                            ) : (
                                <>
                                    <DownloadIcon24 fill="#121212" />
                                    <FormattedMessage id={"reports:download"} />
                                </>
                            )}
                        </PrimaryButton>
                    </Container>
                </Wrapper>
            </Page>
        </VRIntlProviderComponent>
    );
};

export default Reports;
